import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { inject, NgModule } from '@angular/core';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateCacheModule, TranslateCacheService, TranslateCacheSettings } from 'ngx-translate-cache';
import { I18nService } from '../services';
import { AppUtils } from '../utils/app.utils';
import { LocalStorageUtils } from '../utils/localStorage.utils';
import { StringUtils } from '../utils/string.utils';
import { UrlUtils } from '../utils/url.utils';
import { TranslateHttpLoader } from './translate-http.loader';

@NgModule({
  declarations: [],
  imports: [
    CommonModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateLoaderFactory,
        deps: [HttpClient],
      },
    }),
    TranslateCacheModule.forRoot({
      cacheService: {
        provide: TranslateCacheService,
        useFactory: translateCacheFactory,
        deps: [TranslateService, TranslateCacheSettings],
      },
    }),
  ],
  exports: [TranslateModule],
})
export class I18nModule {
  private readonly i18nService: I18nService = inject(I18nService);

  constructor() {
    const translate: TranslateService = inject(TranslateService);
    const translateCacheService: TranslateCacheService = inject(TranslateCacheService);

    const url: string = StringUtils.updatePathname(globalThis.location.pathname);
    const defaultLanguage: string = this.i18nService.getDefaultLanguage();
    let allLanguages: string[] = this.i18nService.getShopAllLanguages();
    let language: string = '';
    let storeId: string = '';

    if (I18nService.languageUrlRegex.exec(url)) {
      const locale: string = UrlUtils.getLocaleFromPathName(url);
      const store: any = this.i18nService.getStoreByLocale(locale);

      if (store) {
        const [languageCode]: string[] = locale.split('-');
        let storeLanguage: any = store.languages.find((lang: any) => lang.code === languageCode);

        // in case of unused lang or typo (e.g. ee) set default language
        if (!storeLanguage) {
          storeLanguage = store.languages.find((lang: any) => lang.code === defaultLanguage);
        }

        language = storeLanguage.id || defaultLanguage;
        storeId = store.storeId;
      } else {
        language = defaultLanguage;
      }
    } else {
      const storeIdFromStorage: string = AppUtils.getStoreIdFromLocalStorage();
      const cachedLanguage: string = translateCacheService.getCachedLanguage();

      language = !cachedLanguage || cachedLanguage === 'undefined' ? defaultLanguage : cachedLanguage;
      storeId = storeIdFromStorage;
    }

    allLanguages = allLanguages.length === 0 ? [language] : allLanguages;
    translate.addLangs(allLanguages);

    LocalStorageUtils.setKeyValue('storeId', storeId);
    LocalStorageUtils.setKeyValue('lang', language);

    translateCacheService.init();
  }
}

export function translateLoaderFactory(httpClient: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(httpClient);
}

export function translateCacheFactory(
  translateService: TranslateService,
  translateCacheSettings: TranslateCacheSettings,
): TranslateCacheService {
  return new TranslateCacheService(translateService, translateCacheSettings);
}
